import { useState } from "react";
import Sidebar from "../../components/feature/Sidebar";
import TopBar from "../../components/feature/TopBar";
import HubTaskPanel from "../../components/feature/HubTaskPanel";
import { concertTasks, concertCollabTasks } from "../../mocks/hubTasks";

const allConcertTasks = [...concertTasks, ...concertCollabTasks];
import { useCompany } from "../../contexts/CompanyContext";

type Tab = "tasks" | "lineup" | "budget" | "tickets";

const lineupArtists = [
  { name: "루나&스타즈", genre: "K-POP", fee: 8000, status: "확정", stage: "메인스테이지" },
  { name: "DarkNova", genre: "R&B/Hip-hop", fee: 5500, status: "협상중", stage: "세컨드스테이지" },
  { name: "VELVET", genre: "인디팝", fee: 2000, status: "검토중", stage: "인디스테이지" },
  { name: "M.O.N.G.", genre: "밴드", fee: 3200, status: "확정", stage: "세컨드스테이지" },
  { name: "솔로이스트 K", genre: "발라드", fee: 4500, status: "확정", stage: "메인스테이지" },
];

const sponsors = [
  { company: "서울전자", category: "타이틀 스폰서", amount: 30000, status: "계약완료" },
  { company: "하이트진로", category: "공식 음료", amount: 8000, status: "계약완료" },
  { company: "인스타그램 코리아", category: "미디어 파트너", amount: 5000, status: "협상중" },
  { company: "CJ ENM", category: "방송 중계권", amount: 12000, status: "검토중" },
];

const ticketData = [
  { tier: "VIP 패키지", price: 250000, capacity: 500, sold: 423, revenue: 10575 },
  { tier: "스탠딩 A", price: 150000, capacity: 2000, sold: 1870, revenue: 28050 },
  { tier: "스탠딩 B", price: 99000, capacity: 3000, sold: 2640, revenue: 26136 },
  { tier: "지정석", price: 120000, capacity: 1500, sold: 980, revenue: 11760 },
];

export default function ConcertHub() {
  const { workspaceId } = useCompany();
  const [activeTab, setActiveTab] = useState<Tab>("tasks");

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: "tasks", label: "내 할 일", icon: "ri-checkbox-multiple-line" },
    { id: "lineup", label: "아티스트 라인업", icon: "ri-mic-line" },
    { id: "budget", label: "공연 예산", icon: "ri-money-dollar-circle-line" },
    { id: "tickets", label: "티켓 판매 현황", icon: "ri-ticket-line" },
  ];

  const totalTicketRevenue = ticketData.reduce((sum, t) => sum + t.revenue, 0);
  const totalSponsor = sponsors.filter(s => s.status === "계약완료").reduce((sum, s) => sum + s.amount, 0);
  const avgOccupancy = Math.round(ticketData.reduce((sum, t) => sum + (t.sold / t.capacity) * 100, 0) / ticketData.length);

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex font-sans">
      <Sidebar />
      <div className="flex-1 ml-56 flex flex-col">
        <TopBar />
        <main className="flex-1 p-5">
          {/* Header */}
          <div className="bg-[#111112] border border-white/[0.06] rounded-xl px-5 py-4 flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#4B6CF7]/10 border border-[#4B6CF7]/20">
                <i className="ri-mic-line text-[#7A9BFF] text-lg"></i>
              </div>
              <div>
                <h1 className="text-[#F2F2F2] font-semibold text-sm">공연기획 허브</h1>
                <p className="text-[#444444] text-xs">라인업 · 티켓 판매 · 스폰서 · 예산 집행</p>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <div className="text-right">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest">객석 점유율</div>
                <div className="text-[#F2F2F2] font-semibold text-base">{avgOccupancy}%</div>
              </div>
              <div className="text-right">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest">티켓 수익</div>
                <div className="text-[#888888] font-semibold text-base">{totalTicketRevenue.toLocaleString()}만원</div>
              </div>
              <div className="text-right">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest">스폰서 수익</div>
                <div className="text-[#888888] font-semibold text-base">{totalSponsor.toLocaleString()}만원</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-0.5 mb-5 bg-white/[0.03] rounded-xl p-1 w-fit border border-white/[0.05]">
            {tabs.map(tab => (
              <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id ? "bg-[#4B6CF7]/15 text-[#7A9BFF] border border-[#4B6CF7]/20" : "text-[#444444] hover:text-[#888888]"
                }`}>
                <i className={`${tab.icon} text-sm`}></i>{tab.label}
              </button>
            ))}
          </div>

          {activeTab === "tasks" && <HubTaskPanel tasks={allConcertTasks} hubTitle="공연기획" workspaceId={workspaceId} hub="concert" />}

          {activeTab === "lineup" && (
            <div className="space-y-4">
              <div className="bg-[#111112] border border-white/[0.06] rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-white/[0.05] flex items-center justify-between">
                  <span className="text-[#888888] text-xs font-medium uppercase tracking-widest">아티스트 라인업</span>
                  <button type="button" className="text-xs px-3 py-1.5 rounded-lg bg-[#4B6CF7]/10 text-[#7A9BFF] border border-[#4B6CF7]/20 cursor-pointer whitespace-nowrap hover:bg-[#4B6CF7]/20 transition-colors">
                    <i className="ri-add-line mr-1"></i>아티스트 추가
                  </button>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/[0.04]">
                      {["아티스트", "장르", "출연료", "무대", "상태"].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-[#3D3D3D] font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.03]">
                    {lineupArtists.map((a) => (
                      <tr key={a.name} className="hover:bg-white/[0.02] transition-colors">
                        <td className="px-5 py-3 text-[#CCCCCC] font-medium">{a.name}</td>
                        <td className="px-5 py-3 text-[#555555]">{a.genre}</td>
                        <td className="px-5 py-3 text-[#888888] tabular-nums">{a.fee.toLocaleString()}만원</td>
                        <td className="px-5 py-3 text-[#555555]">{a.stage}</td>
                        <td className="px-5 py-3">
                          <span className={`text-[10px] px-2 py-0.5 rounded border ${
                            a.status === "확정" ? "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20" :
                            a.status === "협상중" ? "text-[#F2F2F2]/50 bg-white/[0.05] border-white/10" :
                            "text-[#555555] border-white/[0.05]"
                          }`}>{a.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-[#111112] border border-white/[0.06] rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-white/[0.05]">
                  <span className="text-[#888888] text-xs font-medium uppercase tracking-widest">스폰서 현황</span>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/[0.04]">
                      {["기업명", "카테고리", "금액", "상태"].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-[#3D3D3D] font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.03]">
                    {sponsors.map((s) => (
                      <tr key={s.company} className="hover:bg-white/[0.02] transition-colors">
                        <td className="px-5 py-3 text-[#CCCCCC] font-medium">{s.company}</td>
                        <td className="px-5 py-3 text-[#555555]">{s.category}</td>
                        <td className="px-5 py-3 text-[#888888] tabular-nums">{s.amount.toLocaleString()}만원</td>
                        <td className="px-5 py-3">
                          <span className={`text-[10px] px-2 py-0.5 rounded border ${
                            s.status === "계약완료" ? "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20" :
                            s.status === "협상중" ? "text-[#F2F2F2]/50 bg-white/[0.05] border-white/10" :
                            "text-[#555555] border-white/[0.05]"
                          }`}>{s.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "tickets" && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 mb-2">
                {[
                  { label: "총 티켓 수익", value: `${totalTicketRevenue.toLocaleString()}만원`, icon: "ri-ticket-line" },
                  { label: "평균 점유율", value: `${avgOccupancy}%`, icon: "ri-user-3-line" },
                  { label: "총 판매 매수", value: `${ticketData.reduce((s, t) => s + t.sold, 0).toLocaleString()}장`, icon: "ri-bar-chart-line" },
                ].map(stat => (
                  <div key={stat.label} className="bg-[#111112] border border-white/[0.06] rounded-xl p-4 flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#4B6CF7]/10">
                      <i className={`${stat.icon} text-[#7A9BFF] text-base`}></i>
                    </div>
                    <div>
                      <div className="text-[#3D3D3D] text-[10px]">{stat.label}</div>
                      <div className="text-[#F2F2F2] text-base font-semibold">{stat.value}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-[#111112] border border-white/[0.06] rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/[0.04]">
                      {["티켓 등급", "가격", "정원", "판매", "점유율", "매출"].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-[#3D3D3D] font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.03]">
                    {ticketData.map((t) => {
                      const occ = Math.round((t.sold / t.capacity) * 100);
                      return (
                        <tr key={t.tier} className="hover:bg-white/[0.02]">
                          <td className="px-5 py-3 text-[#CCCCCC] font-medium">{t.tier}</td>
                          <td className="px-5 py-3 text-[#888888]">{t.price.toLocaleString()}원</td>
                          <td className="px-5 py-3 text-[#555555]">{t.capacity.toLocaleString()}</td>
                          <td className="px-5 py-3 text-[#888888]">{t.sold.toLocaleString()}</td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-20 bg-white/[0.04] rounded-full h-1">
                                <div className="h-1 rounded-full bg-[#4B6CF7]/50" style={{ width: `${occ}%` }} />
                              </div>
                              <span className="text-[#666666]">{occ}%</span>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-[#CCCCCC] tabular-nums">{t.revenue.toLocaleString()}만원</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "budget" && (
            <div className="bg-[#111112] border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-white/[0.05]">
                <span className="text-[#888888] text-xs font-medium uppercase tracking-widest">공연 예산 집행 현황</span>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/[0.04]">
                    {["항목", "예산", "집행", "잔액", "진행도"].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-[#3D3D3D] font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.03]">
                  {[
                    { item: "아티스트 출연료", budget: 25000, spent: 18500 },
                    { item: "무대 제작비", budget: 15000, spent: 9800 },
                    { item: "음향·조명 장비", budget: 8000, spent: 7200 },
                    { item: "홍보·마케팅비", budget: 5000, spent: 3100 },
                    { item: "공연장 대관료", budget: 12000, spent: 12000 },
                    { item: "스태프 인건비", budget: 6000, spent: 3500 },
                  ].map(row => {
                    const pct = Math.round((row.spent / row.budget) * 100);
                    return (
                      <tr key={row.item} className="hover:bg-white/[0.02]">
                        <td className="px-5 py-3 text-[#CCCCCC]">{row.item}</td>
                        <td className="px-5 py-3 text-[#888888] tabular-nums">{row.budget.toLocaleString()}만원</td>
                        <td className="px-5 py-3 text-[#CCCCCC] tabular-nums">{row.spent.toLocaleString()}만원</td>
                        <td className="px-5 py-3 text-[#555555] tabular-nums">{(row.budget - row.spent).toLocaleString()}만원</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-white/[0.04] rounded-full h-1">
                              <div className={`h-1 rounded-full ${pct >= 100 ? 'bg-white/30' : pct >= 80 ? 'bg-white/20' : 'bg-[#4B6CF7]/50'}`}
                                style={{ width: `${Math.min(pct, 100)}%` }} />
                            </div>
                            <span className="text-[#444444]">{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
